import { RouterOutlined, WarningOutlined } from '@mui/icons-material'
import { Box, CircularProgress, Link, Stack } from '@mui/material'
import type { UseQueryResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { ControllerRouteInformation } from '@/features/controller/constants'
import type { ControllerDescription, ControllerListIdentifier } from '@/features/controller/types'

interface ControllerListHeaderProps {
  controllerListIdentifier: ControllerListIdentifier
  controllerQueryResult?: UseQueryResult<ControllerDescription | null, Error>
}

function renderControllerLink(
  uuid: string | undefined,
  equipmentNumber: number | undefined,
  controller: ControllerDescription | null,
) {
  if (controller) {
    return (
      <Link href={ControllerRouteInformation.CONTROLLER_DETAILS.navigationPath(controller.id)}>
        {controller.equipmentNumber}
      </Link>
    )
  }
  return uuid ? (
    <Link href={ControllerRouteInformation.CONTROLLER_DETAILS.navigationPath(uuid!)}>{uuid}</Link>
  ) : (
    <Link href={ControllerRouteInformation.CONTROLLER_BY_EQUIPMENT_NUMBER.navigationPath(equipmentNumber!)}>
      {equipmentNumber}
    </Link>
  )
}

export function ControllerListHeader({
  controllerListIdentifier,
  controllerQueryResult,
}: Readonly<ControllerListHeaderProps>) {
  const { t } = useTranslation()
  const { equipmentNumber, rawValue, id } = controllerListIdentifier

  if (!controllerQueryResult) {
    return (
      <Stack alignItems="center" direction="row" gap={1}>
        <CircularProgress size={24} title="Loading controller" />
        {renderControllerLink(id, equipmentNumber, null)}
      </Stack>
    )
  }

  const { data, isLoading, isSuccess, error } = controllerQueryResult

  const controllerDescription = data ? (data as ControllerDescription) : null

  if (error) {
    let message = error.message
    if (isAxiosError(error) && (error as AxiosError).status === 403) {
      message = `No permission to access: ${rawValue}`
    }

    return (
      <Stack alignItems="center" direction="row" gap={1}>
        <WarningOutlined color="warning" />
        <Box>{message}</Box>
      </Stack>
    )
  }

  if (!isLoading && !controllerDescription) {
    // Emulated, virtual, cloud and legacy controllers are not in Device Manager, but their page still
    // has metrics and logs. A UUID Device Manager does not know is simply wrong, so it stays a warning.
    // Only a successful lookup says Device Manager answered 404; a query paused offline has no data either.
    if (isSuccess && !id && equipmentNumber !== undefined) {
      return (
        <Stack alignItems="center" direction="row" gap={1}>
          <RouterOutlined color="disabled" />
          {renderControllerLink(id, equipmentNumber, null)}
          <CustomTypography color="text.secondary" variant="caption">
            {t('controllers.bulk_actions.grid.not_registered')}
          </CustomTypography>
        </Stack>
      )
    }

    return (
      <Stack alignItems="center" direction="row" gap={1}>
        <WarningOutlined color="warning" />
        <Box>Controller not found: {rawValue}</Box>
      </Stack>
    )
  }

  return (
    <Stack alignItems="center" direction="row" gap={1}>
      {isLoading ? <CircularProgress size={24} title="Loading controller" /> : <RouterOutlined />}
      {renderControllerLink(id, equipmentNumber, controllerDescription)}
    </Stack>
  )
}
